/**
 * mpRemoteChampions — spawn/update other players (and bots) from MP match/snapshot.
 *
 * Smooth 60+ FPS linear/angular interpolation over 20Hz server snapshots via JitterBuffer.
 */

import { poseForMpPlayer } from '../../domain/net/mpSpawnPoses.js';
import { createJitterBuffer } from '../../application/net/jitterBuffer.js';
import { interpolateSnapshots } from '../../application/net/stateInterpolator.js';

export function listRemotePlayers(players = [], localClientId) {
  return (players || []).filter((p) => p && p.clientId !== localClientId);
}

export function meshTeamForLobbyTeam(team) {
  if (team === 'chaos' || team === 'red') return 'red';
  return 'blue';
}

export function applyRemoteFowVisibility(remotes, entities, localClientId) {
  const present = new Set();
  for (const e of entities || []) {
    const id = e?.ownerId || e?.id;
    if (id && id !== localClientId) present.add(id);
  }
  for (const [id, entry] of remotes) {
    if (entry?.group) entry.group.visible = present.has(id);
  }
}

/**
 * @param {{
 *   scene: { add: Function, remove: Function },
 *   createGroup: () => { add: Function, position: { set: Function } },
 *   createMesh: (championKey: string, meshTeam: string) => object
 * }} deps
 */
export function createMpRemoteChampionsController(deps) {
  const { scene, createGroup, createMesh } = deps;
  const remotes = new Map();
  const jitterBuffer = createJitterBuffer({ targetDelayMs: 50 });

  function clear() {
    for (const entry of remotes.values()) {
      if (scene && entry.group) scene.remove(entry.group);
    }
    remotes.clear();
    jitterBuffer.clear();
  }

  function syncFromMatch(match, localClientId) {
    clear();
    const all = match?.players || [];
    const list = listRemotePlayers(all, localClientId);
    for (const p of list) {
      const group = createGroup();
      const key = p.championId || 'ashe';
      const meshTeam = meshTeamForLobbyTeam(p.team);
      const mesh = createMesh(key, meshTeam);
      if (mesh && typeof group.add === 'function') group.add(mesh);
      const index = all.findIndex((row) => row.clientId === p.clientId);
      const pose = poseForMpPlayer(match.mapId, all, index >= 0 ? index : 0);
      if (group.position && typeof group.position.set === 'function') {
        group.position.set(pose.x, 0, pose.z);
      }
      if (scene) scene.add(group);
      remotes.set(p.clientId, { group, championId: key, clientId: p.clientId });
    }
    return remotes.size;
  }

  function applySnapshot(snapshot, localClientId) {
    if (!snapshot) return;
    jitterBuffer.push(snapshot);

    // Direct fallback placement if jitterBuffer has only 1 snapshot
    if (jitterBuffer.getBufferedCount() === 1) {
      const entities = snapshot.entities || [];
      for (const e of entities) {
        if (!e) continue;
        const targetId = e.ownerId || e.id;
        if (targetId === localClientId) continue;
        const entry = remotes.get(targetId);
        if (entry?.group?.position?.set) {
          entry.group.position.set(e.x || 0, 0, e.z !== undefined ? e.z : (e.y || 0));
        }
      }
    }
    if (!snapshot.isDelta) {
      applyRemoteFowVisibility(remotes, snapshot.entities, localClientId);
    }
  }

  /**
   * Smoothly interpolates remote positions frame-by-frame at 60+ FPS.
   * @param {number} [renderTime]
   */
  function updateFrame(renderTime = Date.now()) {
    const pair = jitterBuffer.getInterpolationPair(renderTime);
    if (!pair) return;

    const interpolated = interpolateSnapshots(pair.snapA, pair.snapB, pair.renderTime);
    if (!interpolated || !Array.isArray(interpolated.entities)) return;

    for (const e of interpolated.entities) {
      const targetId = e.ownerId || e.id;
      const entry = remotes.get(targetId);
      if (entry?.group?.position?.set) {
        entry.group.position.set(e.x || 0, 0, e.z !== undefined ? e.z : (e.y || 0));
      }
    }
  }

  function placeLocalFromSnapshot(snapshot, localClientId, localEntity) {
    if (!localEntity) return false;
    const mine = (snapshot?.entities || []).find((e) => (e.ownerId || e.id) === localClientId);
    if (!mine) return false;
    localEntity.x = mine.x;
    localEntity.z = mine.z !== undefined ? mine.z : (mine.y || 0);
    return true;
  }

  return {
    syncFromMatch,
    applySnapshot,
    updateFrame,
    placeLocalFromSnapshot,
    clear,
    getRemotes: () => remotes
  };
}
